import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, X } from 'lucide-react';
import {
  addBodyMember, createOrganization, deleteOrganization, fetchBodies, fetchOrganizations,
  removeBodyMember, searchPeople,
} from './ProgramAdminApi';
import {
  Actions, Button, Card, CardTitle, Field, FormGrid, Input, List, ListItem, Message, Select,
} from './ui';
import { EmptyDay, MiniButton, PersonMeta, PersonName } from './programAdminStyles';

// The "Organizers & committee" area (plan §7.7 item 3, design §4.6). The event bodies - organizers,
// scientific committee, scientific advisory board - each a list of people and organizations; plus
// the organizations themselves. Everything is a small list plus the actions that change it; the
// sign-off of the invitation mails is generated from these lists, so this is where they are kept.
const BODY_CHOICES = [
  { value: 'ORGANIZER', id: 'programAdmin.organizers.body.organizer', def: 'Organizers' },
  { value: 'SCIENTIFIC_COMMITTEE', id: 'programAdmin.organizers.body.committee', def: 'Scientific committee' },
  { value: 'SCIENTIFIC_ADVISORY_BOARD', id: 'programAdmin.organizers.body.board', def: 'Scientific advisory board' },
];

// The backend answers a refusal (409 already in the body, duplicate organization name) with a plain
// text body; anything else falls back to the caller's message.
const responseText = (err, fallback) => (
  typeof err?.response?.data === 'string' && err.response.data ? err.response.data : fallback
);

const OrganizersArea = ({ eventId }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [bodies, setBodies] = useState([]);
  const [bodiesLoading, setBodiesLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [removingOrgId, setRemovingOrgId] = useState(null);

  const loadBodies = useCallback(async () => {
    try {
      setBodies((await fetchBodies(eventId)) || []);
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(responseText(err, intl.formatMessage({
        id: 'programAdmin.organizers.loadFailed', defaultMessage: 'Could not load the event bodies.',
      })));
    }
  }, [eventId, intl, navigate]);

  useEffect(() => {
    if (eventId == null) { setBodiesLoading(false); return undefined; }
    let alive = true;
    setBodiesLoading(true);
    loadBodies().finally(() => { if (alive) setBodiesLoading(false); });
    return () => { alive = false; };
  }, [eventId, loadBodies]);

  const loadOrganizations = useCallback(async () => {
    try {
      setOrganizations((await fetchOrganizations()) || []);
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(responseText(err, intl.formatMessage({
        id: 'programAdmin.organizers.orgsFailed', defaultMessage: 'Could not load the organizations.',
      })));
    } finally {
      setOrganizationsLoading(false);
    }
  }, [intl, navigate]);

  useEffect(() => { loadOrganizations(); }, [loadOrganizations]);

  const removeMember = async (id) => {
    setError(null);
    setRemovingId(id);
    try {
      await removeBodyMember(eventId, id);
      await loadBodies();
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(responseText(err, intl.formatMessage({
        id: 'programAdmin.organizers.removeFailed', defaultMessage: 'Could not remove this member.',
      })));
    } finally {
      setRemovingId(null);
    }
  };

  // ---------------------------------------------------------------- add a member

  const [body, setBody] = useState(BODY_CHOICES[0].value);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [person, setPerson] = useState(null);
  const [organizationId, setOrganizationId] = useState('');
  const [adding, setAdding] = useState(false);
  const searchSeq = useRef(0);

  // Debounced person search (person.PersonAdminController search): the same "pick a person" box the
  // other admin screens use, so a person who is not at this event yet can be found too.
  useEffect(() => {
    const mine = ++searchSeq.current;
    const q = query.trim();
    if (!q) { setHits([]); setSearching(false); return undefined; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchPeople(q, 20)
        .then((rows) => { if (searchSeq.current === mine) setHits(rows || []); })
        .catch((err) => {
          if (searchSeq.current !== mine) return;
          setHits([]);
          if (err?.response?.status === 401) { navigate('/admin/login'); return; }
          setError(responseText(err, intl.formatMessage({
            id: 'programAdmin.organizers.searchFailed', defaultMessage: 'Could not search for people.',
          })));
        })
        .finally(() => { if (searchSeq.current === mine) setSearching(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [query, intl, navigate]);

  const pickPerson = (hit) => {
    setPerson(hit);
    setOrganizationId('');
    setQuery('');
    setHits([]);
  };

  const pickOrganization = (value) => {
    setOrganizationId(value);
    if (value) { setPerson(null); setQuery(''); setHits([]); }
  };

  const submitMember = async (e) => {
    e.preventDefault();
    const chosen = person
      ? { personId: person.id, body }
      : organizationId
        ? { organizationId, body }
        : null;
    if (!chosen) {
      setError(intl.formatMessage({
        id: 'programAdmin.organizers.pickOne', defaultMessage: 'Pick a person or an organization first.',
      }));
      return;
    }
    setError(null);
    setAdding(true);
    try {
      await addBodyMember(eventId, chosen);
      setPerson(null);
      setOrganizationId('');
      setQuery('');
      setHits([]);
      await loadBodies();
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(responseText(err, intl.formatMessage({
        id: 'programAdmin.organizers.addFailed', defaultMessage: 'Could not add this member.',
      })));
    } finally {
      setAdding(false);
    }
  };

  // ---------------------------------------------------------------- organizations

  const [orgName, setOrgName] = useState('');
  const [orgWebsite, setOrgWebsite] = useState('');
  const [orgLogo, setOrgLogo] = useState('');
  const [creating, setCreating] = useState(false);

  const submitOrganization = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) {
      setError(intl.formatMessage({
        id: 'programAdmin.organizers.orgNameRequired', defaultMessage: 'An organization needs a name.',
      }));
      return;
    }
    setError(null);
    setCreating(true);
    try {
      await createOrganization({
        name: orgName.trim(),
        websiteUrl: orgWebsite.trim() || null,
        logoUrl: orgLogo.trim() || null,
      });
      setOrgName('');
      setOrgWebsite('');
      setOrgLogo('');
      await loadOrganizations();
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(responseText(err, intl.formatMessage({
        id: 'programAdmin.organizers.orgCreateFailed', defaultMessage: 'Could not create this organization.',
      })));
    } finally {
      setCreating(false);
    }
  };

  const removeOrganization = async (id) => {
    setError(null);
    setRemovingOrgId(id);
    try {
      await deleteOrganization(id);
      setOrganizationId((current) => (String(current) === String(id) ? '' : current));
      await loadOrganizations();
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(responseText(err, intl.formatMessage({
        id: 'programAdmin.organizers.orgDeleteFailed', defaultMessage: 'Could not delete this organization.',
      })));
    } finally {
      setRemovingOrgId(null);
    }
  };

  // One section per body, in print order; a member whose body is not one of the three known values
  // is shown under its raw value rather than dropped.
  const sections = useMemo(() => {
    const known = new Set(BODY_CHOICES.map((choice) => choice.value));
    const list = BODY_CHOICES.map((choice) => ({
      key: choice.value, labelId: choice.id, def: choice.def,
      members: bodies.filter((member) => member.body === choice.value),
    }));
    bodies.forEach((member) => {
      if (known.has(member.body)) return;
      let section = list.find((entry) => entry.key === member.body);
      if (!section) { section = { key: member.body, labelId: null, def: member.body, members: [] }; list.push(section); }
      section.members.push(member);
    });
    return list;
  }, [bodies]);

  const selectedMember = person || (organizationId ? organizations.find((org) => String(org.id) === organizationId) : null);

  return (
    <div>
      {error && <Message error>{error}</Message>}

      <Card>
        <CardTitle><FormattedMessage id="programAdmin.organizers.title" defaultMessage="Organizers & committee" /></CardTitle>
        <p style={{ margin: 0, fontSize: '.85rem', color: '#475569', lineHeight: 1.6 }}>
          <FormattedMessage id="programAdmin.organizers.intro"
            defaultMessage="The event bodies and the organizations in them. Organizers, the scientific committee and the scientific advisory board, in print order (design §4.6)." />
        </p>
      </Card>

      {bodiesLoading ? (
        <EmptyDay><FormattedMessage id="programAdmin.organizers.loading" defaultMessage="Loading organizers and committee…" /></EmptyDay>
      ) : sections.map((section) => (
        <Card key={section.key}>
          <CardTitle>
            {section.labelId
              ? <FormattedMessage id={section.labelId} defaultMessage={section.def} />
              : section.def}
            <span style={{ marginLeft: '.5rem', fontSize: '.8rem', fontWeight: 600, color: '#64748b' }}>
              ({section.members.length})
            </span>
          </CardTitle>
          {section.members.length === 0 ? (
            <p style={{ margin: 0, fontSize: '.82rem', color: '#64748b' }}>
              <FormattedMessage id="programAdmin.organizers.bodyEmpty" defaultMessage="Nobody in this body yet." />
            </p>
          ) : (
            <List>
              {section.members.map((member) => (
                <ListItem key={member.id}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <PersonName>{member.name || <FormattedMessage id="programAdmin.organizers.unnamed" defaultMessage="(no name)" />}</PersonName>
                    {member.organizationId != null && (
                      <PersonMeta>
                        <FormattedMessage id="programAdmin.organizers.organizationTag" defaultMessage="Organization" />
                      </PersonMeta>
                    )}
                  </div>
                  <MiniButton $danger type="button" disabled={removingId === member.id} onClick={() => removeMember(member.id)}>
                    <Trash2 size={12} aria-hidden="true" />
                    <FormattedMessage id="programAdmin.organizers.remove" defaultMessage="Remove" />
                  </MiniButton>
                </ListItem>
              ))}
            </List>
          )}
        </Card>
      ))}

      <Card>
        <CardTitle><FormattedMessage id="programAdmin.organizers.addTitle" defaultMessage="Add a member" /></CardTitle>
        <form onSubmit={submitMember}>
          <FormGrid>
            <Field>
              <FormattedMessage id="programAdmin.organizers.bodyLabel" defaultMessage="Body" />
              <Select value={body} onChange={(e) => setBody(e.target.value)}>
                {BODY_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {intl.formatMessage({ id: choice.id, defaultMessage: choice.def })}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <FormattedMessage id="programAdmin.organizers.organizationLabel" defaultMessage="Organization" />
              <Select value={organizationId} onChange={(e) => pickOrganization(e.target.value)}>
                <option value="">
                  {intl.formatMessage({ id: 'programAdmin.organizers.noOrganization', defaultMessage: 'No organization' })}
                </option>
                {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
              </Select>
            </Field>
          </FormGrid>

          <div style={{ marginTop: '.9rem' }}>
            <div style={{ fontSize: '.82rem', fontWeight: 600, color: '#334155', marginBottom: '.35rem' }}>
              <FormattedMessage id="programAdmin.organizers.personLabel" defaultMessage="Person" />
            </div>
            {selectedMember ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
                <strong style={{ color: '#111D4E' }}>{selectedMember.programName || selectedMember.name}</strong>
                <MiniButton type="button" onClick={() => { setPerson(null); setOrganizationId(''); }}>
                  <X size={12} aria-hidden="true" />
                  <FormattedMessage id="programAdmin.organizers.clearPick" defaultMessage="Clear" />
                </MiniButton>
              </div>
            ) : (
              <>
                <div style={{ position: 'relative' }}>
                  <Search size={14} aria-hidden="true" style={{ position: 'absolute', left: '.6rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <Input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={intl.formatMessage({ id: 'programAdmin.organizers.personPlaceholder', defaultMessage: 'Search by name, email or institution' })}
                    style={{ width: '100%', paddingLeft: '2rem' }}
                  />
                </div>
                {searching && (
                  <PersonMeta>
                    <FormattedMessage id="programAdmin.organizers.searching" defaultMessage="Searching…" />
                  </PersonMeta>
                )}
                {hits.length > 0 && (
                  <ul style={{ listStyle: 'none', margin: '.5rem 0 0', padding: 0, maxHeight: '12rem', overflowY: 'auto', border: '1px solid #e2e8f0' }}>
                    {hits.slice(0, 20).map((hit) => (
                      <li key={hit.id} style={{ borderBottom: '1px solid #eef2f7' }}>
                        <MiniButton
                          type="button"
                          onClick={() => pickPerson(hit)}
                          style={{ display: 'flex', flexWrap: 'wrap', gap: '.35rem', width: '100%', border: 0, textAlign: 'left' }}
                        >
                          <strong>{hit.programName}</strong>
                          {hit.institution && <span style={{ color: '#64748b', fontWeight: 400 }}>{hit.institution}</span>}
                          {hit.contactEmail && <span style={{ color: '#64748b', fontWeight: 400 }}>{hit.contactEmail}</span>}
                        </MiniButton>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          <Actions>
            <Button type="submit" disabled={adding || !selectedMember}>
              <Plus size={15} aria-hidden="true" />
              <FormattedMessage id="programAdmin.organizers.addSubmit" defaultMessage="Add member" />
            </Button>
          </Actions>
        </form>
      </Card>

      <Card>
        <CardTitle><FormattedMessage id="programAdmin.organizers.organizationsTitle" defaultMessage="Organizations" /></CardTitle>
        {organizationsLoading ? (
          <p style={{ margin: 0, fontSize: '.82rem', color: '#64748b' }}>
            <FormattedMessage id="programAdmin.organizers.orgsLoading" defaultMessage="Loading organizations…" />
          </p>
        ) : organizations.length === 0 ? (
          <p style={{ margin: 0, fontSize: '.82rem', color: '#64748b' }}>
            <FormattedMessage id="programAdmin.organizers.orgsEmpty" defaultMessage="No organizations yet." />
          </p>
        ) : (
          <List>
            {organizations.map((org) => (
              <ListItem key={org.id}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <PersonName>{org.name}</PersonName>
                  {org.websiteUrl && (
                    <PersonMeta>
                      <a href={org.websiteUrl} target="_blank" rel="noreferrer">{org.websiteUrl}</a>
                    </PersonMeta>
                  )}
                </div>
                <MiniButton $danger type="button" disabled={removingOrgId === org.id} onClick={() => removeOrganization(org.id)}>
                  <Trash2 size={12} aria-hidden="true" />
                  <FormattedMessage id="programAdmin.organizers.delete" defaultMessage="Delete" />
                </MiniButton>
              </ListItem>
            ))}
          </List>
        )}
        <form onSubmit={submitOrganization} style={{ marginTop: '1rem', borderTop: '1px solid #eef2f7', paddingTop: '1rem' }}>
          <FormGrid>
            <Field>
              <FormattedMessage id="programAdmin.organizers.orgName" defaultMessage="Name" />
              <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} />
            </Field>
            <Field>
              <FormattedMessage id="programAdmin.organizers.orgWebsite" defaultMessage="Website" />
              <Input value={orgWebsite} onChange={(e) => setOrgWebsite(e.target.value)} />
            </Field>
            <Field>
              <FormattedMessage id="programAdmin.organizers.orgLogo" defaultMessage="Logo URL" />
              <Input value={orgLogo} onChange={(e) => setOrgLogo(e.target.value)} />
            </Field>
          </FormGrid>
          <Actions>
            <Button type="submit" disabled={creating || !orgName.trim()}>
              <Plus size={15} aria-hidden="true" />
              <FormattedMessage id="programAdmin.organizers.orgCreate" defaultMessage="Create organization" />
            </Button>
          </Actions>
        </form>
      </Card>
    </div>
  );
};

export default OrganizersArea;
